import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import Loader from "../../components/Loader/Loader";
import EmptyState from "../../components/EmptyState";
import ThemedTooltip from "../../components/Charts/ThemedTooltip";
import { useGetCampaignVolumeAlertsQuery } from "../../store/api/reportsApi";

const cardStyle = {
  backgroundColor: "var(--bg-elevated)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  boxShadow: "var(--shadow-card)",
};

const SEVERITY_CONFIG = {
  critical: { label: "Dropped to 0", color: "var(--danger)", bg: "var(--danger-soft)" },
  warning: { label: "Volume down", color: "var(--warning)", bg: "var(--warning-soft)" },
  normal: { label: "Stable", color: "var(--success)", bg: "var(--success-soft)" },
};

const SummaryCard = ({ label, value, color, active, onClick }) => (
  <div className="col">
    <div
      style={{ ...cardStyle, cursor: onClick ? "pointer" : "default", outline: active ? `2px solid ${color}` : "none" }}
      className="p-3 h-100"
      onClick={onClick}
    >
      <div className="small" style={{ color: "var(--text-secondary)" }}>
        {label}
      </div>
      <div className="mt-1" style={{ fontSize: "1.6rem", fontWeight: 600, color: color || "var(--text-primary)" }}>
        {value}
      </div>
    </div>
  </div>
);

// Small at-a-glance trend, still with a filled gradient (not a bare line) so
// it reads at a glance, and a hover tooltip so the exact date/count isn't
// hidden - click any row for the full detail chart below.
const Sparkline = ({ data, color, id }) => (
  <div style={{ width: 130, height: 40 }}>
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
        <defs>
          <linearGradient id={`spark-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.4} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <YAxis hide domain={["dataMin", "dataMax"]} />
        <Tooltip content={<ThemedTooltip />} labelFormatter={(v) => v} />
        <Area
          type="monotone"
          dataKey="count"
          name="Calls"
          stroke={color}
          fill={`url(#spark-${id})`}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const VolumeAlerts = () => {
  const { data, error, isLoading, isFetching } = useGetCampaignVolumeAlertsQuery(
    { days: 14, dropThresholdPct: 20 },
    { pollingInterval: 5 * 60 * 1000, refetchOnMountOrArgChange: true }
  );

  const [selectedCampaign, setSelectedCampaign] = useState(null);

  React.useEffect(() => {
    if (error) toast.error("Failed to load campaign volume alerts");
  }, [error]);

  const campaigns = useMemo(() => data?.campaigns || [], [data]);
  const criticalCount = campaigns.filter((c) => c.severity === "critical").length;
  const warningCount = campaigns.filter((c) => c.severity === "warning").length;
  const normalCount = campaigns.length - criticalCount - warningCount;

  // Default to the worst-affected campaign (list is already sorted worst-
  // drop-first) so opening the page immediately shows the thing that needs
  // attention, without requiring a click first.
  const activeCampaign = useMemo(
    () => campaigns.find((c) => c.campaign === selectedCampaign) || campaigns[0] || null,
    [campaigns, selectedCampaign]
  );
  const activeSeverity = SEVERITY_CONFIG[activeCampaign?.severity] || SEVERITY_CONFIG.normal;

  if (isLoading) return <Loader message="Checking campaign volume..." />;

  return (
    <div className="mt-2">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0" style={{ color: "var(--text-primary)" }}>
          Campaign Volume Alerts
        </h5>
        {isFetching && (
          <span className="small" style={{ color: "var(--text-secondary)" }}>
            <span className="spinner-border spinner-border-sm me-2" style={{ color: "var(--accent)" }} />
            Refreshing...
          </span>
        )}
      </div>
      <p className="small mb-3" style={{ color: "var(--text-secondary)" }}>
        Compares each campaign's calls so far today against the same hours yesterday.
        {data?.compareThroughHour != null && (
          <> Comparison covers hours 00:00&ndash;{String(data.compareThroughHour).padStart(2, "0")}:00 EST.</>
        )}
      </p>

      <div className="row row-cols-1 row-cols-sm-3 g-3 mb-3">
        <SummaryCard label="Dropped to 0 calls" value={criticalCount} color="var(--danger)" />
        <SummaryCard label={`Down ${data?.dropThresholdPct ?? 20}%+`} value={warningCount} color="var(--warning)" />
        <SummaryCard label="Stable / Normal" value={normalCount} color="var(--success)" />
      </div>

      {campaigns.length === 0 ? (
        <EmptyState message="No campaign volume data available yet." />
      ) : (
        <>
          {/* Detail chart for whichever campaign is selected below - defaults
              to the worst drop so the page is useful at a glance. */}
          <div style={cardStyle} className="p-3 mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
              <div style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                {activeCampaign?.campaign || "No campaign selected"}
              </div>
              {activeCampaign && (
                <span
                  className="badge rounded-pill"
                  style={{ backgroundColor: activeSeverity.bg, color: activeSeverity.color, fontWeight: 500 }}
                >
                  {activeSeverity.label} &middot; {activeCampaign.percentChange > 0 ? "+" : ""}
                  {activeCampaign.percentChange}%
                </span>
              )}
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={activeCampaign?.dailyCounts || []}>
                <defs>
                  <linearGradient id="detailGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={activeSeverity.color} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={activeSeverity.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" stroke="var(--text-secondary)" tick={{ fontSize: 12 }} />
                <YAxis stroke="var(--text-secondary)" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip content={<ThemedTooltip />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  name="Calls"
                  stroke={activeSeverity.color}
                  fill="url(#detailGradient)"
                  strokeWidth={2}
                  dot={{ r: 3, fill: activeSeverity.color, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div style={cardStyle} className="p-0">
            <div style={{ overflowX: "auto" }}>
              <table className="table table-dark table-hover mb-0" style={{ backgroundColor: "var(--bg-elevated)" }}>
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th>Yesterday (same hours)</th>
                    <th>Today (so far)</th>
                    <th>% Change</th>
                    <th>Last {data?.days ?? 14} days</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => {
                    const sev = SEVERITY_CONFIG[c.severity] || SEVERITY_CONFIG.normal;
                    const isActive = activeCampaign?.campaign === c.campaign;
                    return (
                      <tr
                        key={c.campaign}
                        onClick={() => setSelectedCampaign(c.campaign)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: isActive ? "var(--bg-hover)" : "transparent",
                        }}
                      >
                        <td style={{ color: "var(--text-primary)" }}>{c.campaign}</td>
                        <td>
                          <span
                            className="badge rounded-pill"
                            style={{ backgroundColor: sev.bg, color: sev.color, fontWeight: 500 }}
                          >
                            {sev.label}
                          </span>
                        </td>
                        <td style={{ color: "var(--text-secondary)" }}>{c.yesterdaySameTime}</td>
                        <td style={{ color: "var(--text-primary)" }}>{c.today}</td>
                        <td style={{ color: sev.color, fontWeight: 600 }}>
                          {c.percentChange > 0 ? "+" : ""}
                          {c.percentChange}%
                        </td>
                        <td>
                          <Sparkline data={c.dailyCounts} color={sev.color} id={c.campaign.replace(/\W+/g, "")} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VolumeAlerts;
